// ReportHubFilterContext.js
import { t } from "i18next";
import { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetCarriersSelectQuery } from "../../../newapi/carrier/carrierSlice";
import { useGetInvoiceProductCategorySelectQuery } from "../../../newapi/financial/invoiceProductCategorySlice";
import { useGetInvoiceProductsSelectQuery } from "../../../newapi/financial/invoiceProductSlice";
import { useGetRelationsSelectQuery } from "../../../newapi/global/relation/relationSlice";
import { useGetStreamsSelectQuery } from "../../../newapi/global/stream/streamSlice";
import { useGetShippersSelectQuery } from "../../../newapi/shipper/shipperSlice";
import { useGetAllShipperProfilesSelectQuery } from "../../../newapi/shipper/shipperprofileSlice";
import { useGetProductsSelectQuery } from "../../../newapi/warehouse/productSlice";
import { useGetWarehouseZonesSelectQuery } from "../../../newapi/warehouse/warehouseApiSlice";
import { useGetWarehouseLocationTypesSelectQuery } from "../../../newapi/warehouse/warehouseLocationTypeSlice";
import { useGetUsersSelectQuery } from "../../../newapi/user/userSlice";
import { useGetWebshopCredentialsSelectQuery } from "../../../newapi/webshop/webshopSlice";
import { useGetBiRelationShipperCodesQuery } from "../../../newapi/global/businessIntelligence";
import { countriesFromLocale } from "../../../utils/countries";
import { return_type, scan_statuses, webshopcredentials_webshop_type } from "../../../utils/staticEnums";
import { useSelector } from "react-redux";
import { ENV_TYPES } from "../Sidebar";
import { categoryAllBehaviors, reportFilterComponentTypes, reportFilterDataSource } from "./chartFilterEnums";
import { biEnvironments, chartFilterKeys, reporthubChartOptionKeys } from "./chartOptions";

export const ReportHubFilterContext = createContext({
  filters: {},
  setCurrentReportKey: () => {}, // exposed setter
});


export const ReportHubFilterProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentReportKey, setCurrentReportKey] = useState(null);
  const [chartSettings, setChartSettings] = useState(null);

  // Resolved once so every consumer gets the same answer.
  //   relation logged in                      -> looks at its own numbers
  //   company logged in, isFulfilment true    -> looks across the relations it serves
  //   company logged in, isFulfilment false   -> holds its own stock, so also its own numbers
  const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
  const isFulfilment = useSelector((state) => state.settings.environment.isFulfilment);

  const relationLoggedIn = selectedEnvironmentType === ENV_TYPES[200];
  const companyLoggedIn = selectedEnvironmentType === ENV_TYPES[100];

  let environment = biEnvironments.WAREHOUSE;
  if (relationLoggedIn) {
    environment = biEnvironments.RELATION;
  } else if (companyLoggedIn && isFulfilment) {
    environment = biEnvironments.FULFILMENT;
  }

  const filters = {
    // A relation picks from the carrier codes behind its own shipments, which the backend derives the
    // same way it groups the report; the shippers table holds the warehouse's own records and names,
    // which never reach a relation.
    [chartFilterKeys.SHIPPER]: environment === biEnvironments.RELATION ? {
      name:t("shippers"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetBiRelationShipperCodesQuery,
      queryDataKey:"data", // For those that have select inside .data of the return
      canBeUsedasCategory:true, // for bar charts
      // Values the data carries: a bar for every carrier the relation ever used would drown the
      // handful it shipped with in the period.
      allBehavior: categoryAllBehaviors.PRESENT,
      // The backend hands 'other' over as a code so the frontend can localize it.
      mapData: (rows) => rows.map((row) => row.id === "other" ? { ...row, name: t("other") } : row),
    } : {
      name:t("shippers"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetShippersSelectQuery,
      canBeUsedasCategory:true, // for bar charts
    },
    [chartFilterKeys.SHIPPERPROFILES]: {
      name:t("shipper_profiles"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetAllShipperProfilesSelectQuery,
      canBeUsedasCategory:true, // for bar charts
    },
    [chartFilterKeys.CARRIERS]: {
      name:t("carriers"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetCarriersSelectQuery,
      canBeUsedasCategory:true, // for bar charts
    },
    // Users are held in the root database, not the customer catalog, so the options come from the root
    // API. The report data never needs it: the name of whoever shipped an order is copied onto the log.
    [chartFilterKeys.USERS]: {
      name:t("users"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetUsersSelectQuery,
      canBeUsedasCategory:true, // for bar charts
    },

    [chartFilterKeys.STREAM]: {
      name:t("stream"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetStreamsSelectQuery, 
      queryDataKey:"data", // For those that have select inside .data of the return
      canBeUsedasCategory:true, // for bar charts
    },

    [chartFilterKeys.RELATIONS]: {
      name:t("relations"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetRelationsSelectQuery,
      canBeUsedasCategory:true, // for bar charts
    },

    [chartFilterKeys.INVOICEPRODUCTS]: {
      name:t("invoice_product"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetInvoiceProductsSelectQuery,
      canBeUsedasCategory:true, // for bar charts
    },

    [chartFilterKeys.INVOICEPRODUCTCATEGORIES]: {
      name:t("product_category"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetInvoiceProductCategorySelectQuery,
      canBeUsedasCategory:true, // for bar charts
    },

    [chartFilterKeys.PRODUCTS]: {
      name:t("products"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.SEARCHQUERYDROPDOWN,
      hook: useGetProductsSelectQuery,
      // A catalog runs to thousands of rows, so "all" can only mean the products that actually moved.
      allBehavior: categoryAllBehaviors.PRESENT,
      dropdownOptions: {
        displayName:"combined",
      },
    },


    [chartFilterKeys.WEBSHOPCREDENTIALS]: {
      name:t("webshopcredentials"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetWebshopCredentialsSelectQuery,
      // The pivot builds its rows straight off the credentials table, which has no integrator condition,
      // while the select endpoint drops integrator channels unless asked. Without this the picker offers
      // fewer channels than the pivot shows rows for the very same report.
      hookArgs: {includeIntegrator: true},
      canBeUsedasCategory:true, // for bar charts
      dropdownOptions: {
        // Not "combined": the credentials select only builds that label on the unscoped call, so a
        // relation asking for its own channels gets rows without it and every option renders blank.
        displayName:"description",
        // An order placed by hand has no channel at all. The backend counts those as the null bucket,
        // which the pivot dimension list already offers under the same name.
        extraOptions: [{id: null, description: t("manual")}],
      },
      // Not yet implemented!!!!!!!
      canBeInfluencedBy: {
        [chartFilterKeys.RELATIONS] : {
          // key:"relationId", // only needed if type = 'object' OR 'array
          type:"integer",
          value:'id', // value to use from the object
        }
      }
    },

    [chartFilterKeys.COUNTRIES]: {
      canBeUsedasCategory:true, // for bar charts
      name:t("countries"),
      type: reportFilterDataSource.LOCAL,
      componentType: reportFilterComponentTypes.COUNTRIES,
      // There are ~200 countries and a relation ships to a handful, so "all" means the ones it shipped to.
      allBehavior: categoryAllBehaviors.PRESENT,
    },

    [chartFilterKeys.WEBSHOP_TYPE]: {
      canBeUsedasCategory:true,
      name:t("webshop_type"),
      type: reportFilterDataSource.LOCAL,
      componentType: reportFilterComponentTypes.DROPDOWN,
      getData: () => {
        const locale = i18n.language;
        return Object.keys(webshopcredentials_webshop_type).map((key) => ({
          id: key,
          name: webshopcredentials_webshop_type[key],
          value: key
        }));
      },
    },

    [chartFilterKeys.SCANSTATUS]: {
      canBeUsedasCategory:true,
      name:t("scanstatus"),
      type: reportFilterDataSource.LOCAL,
      componentType: reportFilterComponentTypes.DROPDOWN,
      // Derived by the backend, so there is no table to select from — the four buckets are the enum.
      getData: () => {
        return Object.values(scan_statuses).map((key) => ({
          id: key,
          name: t(key),
          value: key
        }));
      },
    },

    [chartFilterKeys.LOCATION_OCCUPIED]: {
      canBeUsedasCategory:true,
      name:t("occupied"),
      type: reportFilterDataSource.LOCAL,
      componentType: reportFilterComponentTypes.DROPDOWN,
      getData: () => {
        const locale = i18n.language;
        return [
          {
            id:true,
            name:t("occupied_value"),
            value:true,
          },
          {
            id:false,
            name:t("not_occupied"),
            value:false,
          }
        ]
      },
    },

    // [chartFilterKeys.WAREHOUSE_ZONE]: {
    //   name:t("warehouse_location_zone"),
    //   type: reportFilterDataSource.QUERY,
    //   componentType: reportFilterComponentTypes.DROPDOWN,
    //   hook: useGertWarehouse, 
    // },

    [chartFilterKeys.TYPE]: () => {
      
      if(currentReportKey === reporthubChartOptionKeys.RETURNS){
        return {
          name:t("type"),
          type: reportFilterDataSource.LOCAL,
          componentType: reportFilterComponentTypes.DROPDOWN,
          getData: () => {
            return [
              {
                id: return_type.DEFAULT,
                name: t("manually_created"),
                value: return_type.DEFAULT,
              },
              {
                id: return_type.UNANNOUNCED,
                name: t("unannounced"),
                value: return_type.UNANNOUNCED,
              },
              {
                id: return_type.RETURNPORTAL,
                name: t("returnportal"),
                value: return_type.RETURNPORTAL,
              },
              {
                id: return_type.RETURNLESS,
                name: "Returnless",
                value: return_type.RETURNLESS,
              },
              {
                id: return_type.BOL,
                name: "Bol.com",
                value: return_type.BOL,
              },
            ]
          },
        }
      } else if(currentReportKey == reporthubChartOptionKeys.WAREHOUSELOCATIONS) {
        return {
          name:t("warehouse_location_type"),
          type: reportFilterDataSource.QUERY,
          componentType: reportFilterComponentTypes.DROPDOWN,
          dropdownOptions: {
            displayName: "description",
          },
          primaryFilter:{
            timeline:false,
          },
          queryDataKey: "data",
          hook: useGetWarehouseLocationTypesSelectQuery, 
        }
      } 
      else {
        throw Error("Filter [type] not implemented for filterkey " + currentReportKey);
      }
    },

    [chartFilterKeys.WAREHOUSE_ZONE]: {
      name:t("zone"),
      type: reportFilterDataSource.QUERY,
      componentType: reportFilterComponentTypes.DROPDOWN,
      hook: useGetWarehouseZonesSelectQuery, 
      // primaryFilter:{
      //   timeline:false, // disables primary filter ability on timeline
      // },
      dropdownOptions: {
        displayName:"description",
      },
      queryDataKey:"data", // For those that have select inside .data of the return
      canBeUsedasCategory:true, // for bar charts
    },

  };

  return (
    <ReportHubFilterContext.Provider value={{ filters, setCurrentReportKey,chartSettings,setChartSettings,environment }}>
      {children}
    </ReportHubFilterContext.Provider>
  );
};

export const useReportHubFilters = () => {
  const context = useContext(ReportHubFilterContext);
  if (!context) {
    throw new Error(
      "useReportHubFilters must be used within a ReportHubFilterProvider"
    );
  }
  return context;
};
